import {PROPAGATE_STEP_CHANGE} from '~/CreateFabricInvitation/reducers/constants';
import {
  REVERT_TO_BASIC,
  UPDATE_DESIGN_OPTIONS,
  UPDATE_PRISTINE_UYO,
  RESET_DESIGN_OPTIONS,
  SET_UPLOADED_IMAGE,
} from './constants';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {INIT_APP} from '~/common/fabric/constants';
import {silver} from '~libSass/base/_exports.sass';
import {SET_EVITE_EVENT} from '~/common/fabric/reducers/eviteEvent/constants';
import {fabricArtWithHttpsForAllImgPaths} from '~/common/utils/event_utils';
import {deriveIdFromCanvasObject} from '~/CreateFabricInvitation/utils/canvas';

const initialState = {
  originalFabricArt: {},
  fabricArt: {},
  backgroundId: null,
  backgroundColor: silver,
  overlayId: '',
  revealId: '',
  stampId: 'evite01',
  envelopeId: 'wht01',
  linerId: '',
  envelopeStickers: null,
  currentSection: Sections.CARD_DESIGN,
  loaded: false,
  touched: false,
  pristine: true,
  pristineUyo: {},
  pristineBackground: true,
  pristineEnvelopeDesign: true,
  hasTextbox: true,
  editingMobile: false,
  editingUyoMobile: false,
  textAreaOpen: false,
  savingStep: false,
  activeCardEditor: 'intro',
  editorDisabled: false,
  forceShowOnboardingModal: false,
  showMatchingSetPreview: false,
  uyoImagePath: '',
  recentTextColors: [],
  recentBackgroundColors: [],
  usedPremiumFeature: false,
  defaultBackgroundColor: null,
  defaultBackgroundId: null,
  defaultEnvelope: null,
  defaultStamp: null,
  defaultLiner: null,
  shouldTogglePremiumBackground: false,
  init: {},
  uploadedImageUrl: '',
  shouldUseCachedGalleryPhoto: false,
  showSampleTextTooltip: false,
};

function firstMatchingArt(art, categoryList) {
  // eslint-disable-next-line no-restricted-syntax
  for (const c of categoryList) if (c && art[c]) return art[c];
  return undefined;
}

function saveInitValues(state) {
  // eslint-disable-next-line no-param-reassign
  state.init = {
    envelopeId: state.envelopeId,
    backgroundId: state.backgroundId,
    backgroundColor: state.backgroundColor,
    linerId: state.linerId,
    stampId: state.stampId,
    overlayId: state.overlayId,
    revealId: state.revealId,
    envelopeStickers: {...state.envelopeStickers},
  };
}

export const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case RESET_DESIGN_OPTIONS: {
      const options = {};
      for (const option of action.payload) {
        options[option] = state.init[option];
      }
      return {...state, ...options};
    }
    case UPDATE_DESIGN_OPTIONS:
      if (action.payload.savingStep) {
        saveInitValues(state);
      }
      return {...state, ...action.payload};
    case SET_EVITE_EVENT:
      // eslint-disable-next-line no-case-declarations
      const eviteEvent = action.payload;
      return {
        ...state,
        fabricArt: eviteEvent?.fabric_design_data,
        envelopeStickers: eviteEvent?.options?.design?.envelope_fabric_stickers,
        pristine: false,
      };
    case INIT_APP: {
      /* eslint-disable no-case-declarations */
      const template = action.payload.template || {};
      const event = action.payload.event || {};
      const design = event?.options?.design;
      const editableArt = fabricArtWithHttpsForAllImgPaths(template?.editable_art);
      const originalUyoObjects = template?.editable_art
        ? template?.editable_art.default.objects.filter((obj) => obj.type === 'uYOImage')
        : [];
      /* eslint-enable */
      const ret = {
        ...state,
        originalFabricArt: firstMatchingArt(editableArt, [
          new URLSearchParams(window.location.search).get('category_id'),
          'default',
        ]),

        fabricArt:
          event?.fabric_design_data ??
          firstMatchingArt(editableArt, [
            new URLSearchParams(window.location.search).get('category_id'),
            'default',
          ]),
        pristineUyo:
          Object.keys(event).length > 0
            ? Object.fromEntries(
                originalUyoObjects.map((obj) => [deriveIdFromCanvasObject(obj), false])
              )
            : Object.fromEntries(
                originalUyoObjects.map((obj) => [deriveIdFromCanvasObject(obj), true])
              ),
        overlayId: design?.overlay_id ?? '',
        revealId: design?.reveal_id ?? '',
        backgroundId:
          design?.background !== undefined ? design.background : template?.backgrounds?.[0], // allow null without fallback
        defaultBackgroundId:
          design?.background !== undefined ? design.background : template?.backgrounds?.[0], // allow null without fallback
        backgroundColor:
          design?.background_color !== undefined
            ? design.background_color // allow null without fallback
            : (template?.background_color ?? silver),
        defaultBackgroundColor:
          design?.background_color ??
          (template?.background_color ? template?.background_color : silver),
        stampId: design?.stamp ?? template?.stamps?.[0],
        defaultStamp: design?.stamp ?? template?.stamps?.[0],
        envelopeId: design?.envelope ?? template?.envelopes?.[0],
        defaultEnvelope: design?.envelope ?? template?.envelopes?.[0],
        linerId: design?.liner ?? template?.liners?.[0],
        defaultLiner: design?.liner ?? template?.liners?.[0],
        currentSection: Sections.CARD_DESIGN,
        envelopeStickers: design?.envelope_fabric_stickers,
        loaded: false,
        touched: !!event?.id,
        pristine: !event?.id,
        premiumFeaturesPristine: true,
        editingMobile: false,
        editorDisabled: !!event?.id,
        forceShowOnboardingModal: false,
        shouldTogglePremiumBackground: false,
        shouldUseCachedGalleryPhoto: false,
      };
      saveInitValues(ret);
      return ret;
    }
    case UPDATE_PRISTINE_UYO: {
      const newPristineUyo = {...state.pristineUyo};
      newPristineUyo[action.payload.id] = action.payload.pristineUyo;
      const activeCardEditor = action.payload.activeCardEditor || state.activeCardEditor;
      return {...state, pristineUyo: newPristineUyo, activeCardEditor};
    }
    case REVERT_TO_BASIC:
      return {
        ...state,
        usedPremiumFeature: false,
        backgroundId: state.defaultBackgroundId,
        overlayId: '',
        backgroundColor: state.defaultBackgroundColor,
        stampId: state.defaultStamp,
        envelopeId: state.defaultEnvelope,
        linerId: state.defaultLiner,
        envelopeStickers: null,
        premiumFeaturesPristine: true,
        pristineBackground: true,
        shouldTogglePremiumBackground: false,
      };
    case PROPAGATE_STEP_CHANGE:
      return {
        ...state,
        editingMobile: false,
        editingUyoMobile: false,
      };
    case SET_UPLOADED_IMAGE:
      return {
        ...state,
        uploadedImageUrl: action.payload.url,
      };
    default:
      return state;
  }
};
